import {
  CURRENCY_BY_MARKET,
  SEED_MARKETS,
  marketsForScope,
  type Currency,
  type Market,
  type ReferralRewardStatus,
  type Transaction,
  type User,
} from '../dataProvider/types';

/**
 * The Referrals query/aggregation module - the ONE place the referral graph is
 * shaped for the surface. Same seam idiom as subscriptions/earnings: v1
 * aggregates client-side from the mock provider, every selector lives here, and
 * the late real-data migration swaps this module for a server-side endpoint
 * without the surface changing.
 *
 * A referral is a RELATIONSHIP between two users (a referrer and a referee),
 * stored as a one-hop self-FK (`user.referredBy`). The explorer is keyed by the
 * REFERRER: one group per referrer, its referees beneath (one level of grouping,
 * NOT a tree; multi-level depth is deferred, D-referrals).
 *
 * THE THRESHOLD GATE (the anti-abuse mechanism). The referrer earns a reward only
 * once the referee SPENDS a threshold. That spend gate makes mass self-referral
 * under fake emails worthless: an abuser would have to spend real money from each
 * fake account to trigger each reward. The surface DISPLAYS the referee's spend
 * progress toward the threshold so ops can tell a stalled-but-legit referral from
 * an abandoned fake one. Reward CONFERRAL is a future engine's job (watch the
 * threshold, flip state, pay); the admin displays state and can VOID, never pays.
 *
 * BANDING is by the REFERRER's market (the referrer earns the reward, so
 * attribution sits with them). A cross-market referee (different market) is
 * FLAGGED, never hidden, and its progress is approximate because the referee
 * spends in a different currency (the real conversion is the engine's at
 * qualification; this admin does NOT convert).
 */

// --- tunables (flagged for review) -----------------------------------------

/**
 * The spend a referee must reach to QUALIFY the referrer's reward, per market, in
 * that market's currency. uk is GBP 50. us is a PLACEHOLDER round figure, NOT a
 * converted 50: set the real value. [REVIEW: REFERRAL_THRESHOLD]
 */
export const REFERRAL_THRESHOLD: Record<Market, number> = {
  uk: 50, // GBP 50
  us: 70, // USD 70 [REVIEW placeholder - set the real US threshold, not a converted 50]
  ie: 50, // reserved market, not seeded
};

// Spend that counts toward the threshold: settled (confirmed) purchases only, so
// a referee genuinely spent real money (an unconfirmed order does not qualify).
const QUALIFYING_STATUSES: readonly string[] = ['confirmed'];

// --- types ------------------------------------------------------------------

export interface RefereeRow {
  id: number;
  name: string;
  email: string;
  /** The referee's own market. */
  market: Market;
  /** True when the referee's market differs from the referrer's (banding market). */
  crossMarket: boolean;
  rewardStatus: ReferralRewardStatus;
  referredAt: string | null;
  /** The referee's confirmed spend, in the referee's currency. */
  qualifyingSpend: number;
  refereeCurrency: Currency;
  /** The referrer-market threshold the spend is measured against. */
  threshold: number;
  thresholdCurrency: Currency;
  /** Round percent of the threshold the referee has spent. */
  progressPct: number;
  /** True when spend and threshold are different currencies (cross-market): the
   *  percent is approximate, the real conversion is the engine's. */
  approximate: boolean;
  /** Void is offered only on a referral that is not already paid or void. */
  voidable: boolean;
}

export type RewardCounts = Record<ReferralRewardStatus, number>;

export interface ReferrerGroup {
  referrerId: number;
  referrerName: string;
  referrerEmail: string;
  /** The band market (the referrer's market). */
  market: Market;
  refereeCount: number;
  counts: RewardCounts;
  referees: RefereeRow[];
}

export interface ReferralsBand {
  market: Market;
  referrerCount: number;
  totalReferrals: number;
  counts: RewardCounts;
  referrers: ReferrerGroup[];
}

export interface ReferralsView {
  bands: ReferralsBand[];
}

export interface ReferralsInput {
  users: User[];
  transactions: Transaction[];
}

export interface ReferralsOptions {
  /** Access scope (UK | US | GLOBAL); decides which market bands render. */
  scope: string;
}

// --- helpers ----------------------------------------------------------------

/** The market bands a scope renders: scope -> markets, kept to operating markets. */
export const bandsForScope = (scope: string): Market[] =>
  marketsForScope(scope).filter((m) => SEED_MARKETS.includes(m));

const emptyCounts = (): RewardCounts => ({
  pending: 0,
  qualified: 0,
  paid: 0,
  void: 0,
});

const tally = (referees: RefereeRow[]): RewardCounts => {
  const counts = emptyCounts();
  for (const r of referees) counts[r.rewardStatus] += 1;
  return counts;
};

// --- aggregation ------------------------------------------------------------

/** Build the full view for the active scope (one band per derived market). */
export function buildReferralsView(
  input: ReferralsInput,
  opts: ReferralsOptions,
): ReferralsView {
  const usersById = new Map(input.users.map((u) => [u.id, u]));

  // Confirmed spend per user (the referee's qualifying spend).
  const spendByUser = new Map<number, number>();
  for (const t of input.transactions) {
    if (!QUALIFYING_STATUSES.includes(t.status)) continue;
    // An unattributed orphan (userId null) belongs to no member yet, so it counts
    // toward no referrer's spend (D-manual-attribution).
    if (t.userId == null) continue;
    spendByUser.set(
      t.userId,
      (spendByUser.get(t.userId) ?? 0) + Number(t.grossAmount),
    );
  }

  // Group referees (users with an inbound referral) by their referrer.
  const groups = new Map<number, User[]>();
  for (const u of input.users) {
    if (u.referredBy == null || u.referralRewardStatus == null) continue;
    const arr = groups.get(u.referredBy) ?? [];
    arr.push(u);
    groups.set(u.referredBy, arr);
  }

  const buildReferee = (referee: User, referrer: User): RefereeRow => {
    const threshold = REFERRAL_THRESHOLD[referrer.market];
    const qualifyingSpend = spendByUser.get(referee.id) ?? 0;
    const status = referee.referralRewardStatus as ReferralRewardStatus;
    return {
      id: referee.id,
      name: referee.name,
      email: referee.email,
      market: referee.market,
      crossMarket: referee.market !== referrer.market,
      rewardStatus: status,
      referredAt: referee.referredAt,
      qualifyingSpend,
      refereeCurrency: CURRENCY_BY_MARKET[referee.market],
      threshold,
      thresholdCurrency: CURRENCY_BY_MARKET[referrer.market],
      progressPct:
        threshold > 0 ? Math.round((qualifyingSpend / threshold) * 100) : 0,
      approximate: referee.market !== referrer.market,
      voidable: status === 'pending' || status === 'qualified',
    };
  };

  const bands = bandsForScope(opts.scope).map((market) => {
    const referrers: ReferrerGroup[] = [];
    for (const [referrerId, refs] of groups) {
      const referrer = usersById.get(referrerId);
      // Band by the REFERRER's market.
      if (!referrer || referrer.market !== market) continue;
      const referees = refs
        .map((r) => buildReferee(r, referrer))
        .sort((a, b) => a.name.localeCompare(b.name));
      referrers.push({
        referrerId,
        referrerName: referrer.name,
        referrerEmail: referrer.email,
        market,
        refereeCount: referees.length,
        counts: tally(referees),
        referees,
      });
    }
    referrers.sort((a, b) => a.referrerName.localeCompare(b.referrerName));

    const counts = emptyCounts();
    let totalReferrals = 0;
    for (const g of referrers) {
      totalReferrals += g.refereeCount;
      for (const k of Object.keys(counts) as ReferralRewardStatus[]) {
        counts[k] += g.counts[k];
      }
    }

    return {
      market,
      referrerCount: referrers.length,
      totalReferrals,
      counts,
      referrers,
    };
  });

  return { bands };
}
